import { useEffect, useMemo, useState } from "react";

const API =
  process.env.REACT_APP_API_URL || "https://app.nvplaydigital.com";

function montarUrlLogo(logoEmpresa) {
  if (!logoEmpresa) return "";

  const logo = String(logoEmpresa).trim();

  if (!logo) return "";

  let urlFinal = "";

  if (logo.startsWith("http://") || logo.startsWith("https://")) {
    urlFinal = logo;
  } else if (logo.startsWith("/")) {
    urlFinal = `${API}${logo}`;
  } else {
    urlFinal = `${API}/${logo}`;
  }

  const separador = urlFinal.includes("?") ? "&" : "?";

  return `${urlFinal}${separador}v=${Date.now()}`;
}

export default function MinhaRede() {
  const [rede, setRede] = useState([]);
  const [me, setMe] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [excluindoId, setExcluindoId] = useState(null);
  const [busca, setBusca] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    carregar();
  }, []);

  async function carregar() {
    try {
      setCarregando(true);

      const res = await fetch(`${API}/minha-rede`, {
        headers: { Authorization: token },
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Erro ao carregar sua rede.");
        return;
      }

      setRede(data.rede || []);
      setMe(data.usuario || null);
    } catch (err) {
      console.log(err);
      alert("Erro ao carregar sua rede.");
    } finally {
      setCarregando(false);
    }
  }

  async function excluirUsuario(usuario, event) {
    event.stopPropagation();

    const confirmado = window.confirm(
      `Tem certeza que deseja excluir este usuário?\n\nEmpresa: ${
        usuario.nomeEmpresa || "Empresa"
      }\nEmail: ${usuario.email}\n\nEssa ação não pode ser desfeita.`
    );

    if (!confirmado) return;

    try {
      setExcluindoId(usuario.id);

      const res = await fetch(`${API}/usuarios/${usuario.id}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Erro ao excluir usuário.");
        return;
      }

      alert(data.message || "Usuário excluído com sucesso.");
      await carregar();
    } catch (err) {
      console.log(err);
      alert("Erro ao excluir usuário.");
    } finally {
      setExcluindoId(null);
    }
  }

  function abrirPainelUsuario(usuario) {
    if (!usuario?.id) return;

    window.location.href = `/minha-rede/${usuario.id}`;
  }

  function nomeCargo(role) {
    const nomes = {
      master: "Master",
      gestor: "Gestor",
      franquia: "Franquia",
      colaborador: "Colaborador",
    };

    return nomes[role] || role;
  }

  function idParaString(valor) {
    if (!valor) return null;
    if (typeof valor === "string") return valor;
    if (valor.$oid) return valor.$oid;
    return String(valor);
  }

  function normalizar(texto) {
    return String(texto || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const redeFiltrada = useMemo(() => {
    const termo = normalizar(busca);

    if (!termo) return rede;

    return rede.filter((item) => {
      const texto = normalizar(
        `${item.nomeEmpresa} ${item.email} ${item.telefone} ${item.role} ${item.plano}`
      );

      return texto.includes(termo);
    });
  }, [rede, busca]);

  function montarArvore(lista, parentId) {
    return lista
      .filter((item) => idParaString(item.parentId) === idParaString(parentId))
      .map((item) => ({
        ...item,
        filhos: montarArvore(lista, item.id),
      }));
  }

  const arvore = me ? montarArvore(redeFiltrada, me.id) : [];

  const totalPendentes = rede.reduce(
    (acc, item) => acc + Number(item.relatoriosPendentes || 0),
    0
  );

  return (
    <>
      <style>{css}</style>

      <div className="redeContainer">
        <div className="redeHero">
          <div>
            <span className="eyebrow">Painel administrativo</span>
            <h1>Minha Rede</h1>
            <p>
              Acompanhe gestores, franquias, equipes e relatórios recebidos em
              tempo real.
            </p>
          </div>

          <button className="btnAtualizar" onClick={carregar}>
            Atualizar
          </button>
        </div>

        <div className="metricGrid">
          <div className="metricCard">
            <span>Total na rede</span>
            <strong>{rede.length}</strong>
            <p>Usuários ativos abaixo da sua conta</p>
          </div>

          <div className="metricCard destaque">
            <span>Relatórios pendentes</span>
            <strong>{totalPendentes}</strong>
            <p>{totalPendentes > 0 ? "Novo relatório recebido" : "Tudo em dia"}</p>
          </div>

          <div className="metricCard">
            <span>Sua conta</span>
            <strong>{me ? nomeCargo(me.role) : "-"}</strong>
            <p>{me?.nomeEmpresa || "Carregando..."}</p>
          </div>
        </div>

        {me && (
          <div className="boxPrincipal">
            <div className="avatarEmpresa">
              {me.logoEmpresa ? (
                <img src={montarUrlLogo(me.logoEmpresa)} alt={me.nomeEmpresa} />
              ) : (
                <span>{String(me.nomeEmpresa || "N").charAt(0)}</span>
              )}
            </div>

            <div>
              <strong>{me.nomeEmpresa}</strong>
              <p>{me.email}</p>
              <span>{nomeCargo(me.role)}</span>
            </div>
          </div>
        )}

        <div className="barraBusca">
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, empresa, email, gestor ou franquia..."
          />

          {totalPendentes > 0 && (
            <div className="alertaPendencia">
              <span className="bolinha"></span>
              {totalPendentes} relatório(s) pendente(s)
            </div>
          )}
        </div>

        {carregando ? (
          <div className="estadoBox">Carregando rede...</div>
        ) : rede.length === 0 ? (
          <div className="estadoBox">Nenhum usuário na sua rede ainda.</div>
        ) : redeFiltrada.length === 0 ? (
          <div className="estadoBox">Nenhum resultado encontrado.</div>
        ) : arvore.length === 0 && busca ? (
          <div className="listaBusca">
            {redeFiltrada.map((item) => (
              <CardUsuario
                key={item.id}
                item={item}
                nivel={0}
                nomeCargo={nomeCargo}
                excluirUsuario={excluirUsuario}
                excluindoId={excluindoId}
                abrirPainelUsuario={abrirPainelUsuario}
              />
            ))}
          </div>
        ) : (
          <div className="arvoreBox">
            {arvore.map((item) => (
              <No
                key={item.id}
                item={item}
                nivel={0}
                nomeCargo={nomeCargo}
                excluirUsuario={excluirUsuario}
                excluindoId={excluindoId}
                abrirPainelUsuario={abrirPainelUsuario}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function No({
  item,
  nivel,
  nomeCargo,
  excluirUsuario,
  excluindoId,
  abrirPainelUsuario,
}) {
  return (
    <div className="noWrapper" style={{ "--nivel": nivel }}>
      <CardUsuario
        item={item}
        nivel={nivel}
        nomeCargo={nomeCargo}
        excluirUsuario={excluirUsuario}
        excluindoId={excluindoId}
        abrirPainelUsuario={abrirPainelUsuario}
      />

      {item.filhos?.map((filho) => (
        <No
          key={filho.id}
          item={filho}
          nivel={nivel + 1}
          nomeCargo={nomeCargo}
          excluirUsuario={excluirUsuario}
          excluindoId={excluindoId}
          abrirPainelUsuario={abrirPainelUsuario}
        />
      ))}
    </div>
  );
}

function CardUsuario({
  item,
  nomeCargo,
  excluirUsuario,
  excluindoId,
  abrirPainelUsuario,
}) {
  const pendentes = Number(item.relatoriosPendentes || 0);

  return (
    <div className="redeCard" onClick={() => abrirPainelUsuario(item)}>
      {pendentes > 0 && <div className="contadorPendencia">{pendentes}</div>}

      <div className="usuarioAvatar">
        {item.logoEmpresa ? (
          <img src={montarUrlLogo(item.logoEmpresa)} alt={item.nomeEmpresa} />
        ) : (
          <span>{String(item.nomeEmpresa || item.email || "N").charAt(0)}</span>
        )}
      </div>

      <div className="usuarioInfo">
        <strong>{item.nomeEmpresa || "Empresa"}</strong>
        <p>{item.email}</p>

        <div className="badges">
          <span className={`badge status ${item.status || "ativo"}`}>
            {item.status || "ativo"}
          </span>

          <span className="badge cargo">{nomeCargo(item.role)}</span>

          {item.plano && <span className="badge plano">{item.plano}</span>}

          {pendentes > 0 && (
            <span className="badge pendente">Novo relatório</span>
          )}
        </div>
      </div>

      <div className="acoesUsuario">
        <button
          className="btnAbrir"
          onClick={(event) => {
            event.stopPropagation();
            abrirPainelUsuario(item);
          }}
        >
          Abrir painel
        </button>

        <button
          className="btnExcluir"
          onClick={(event) => excluirUsuario(item, event)}
          disabled={excluindoId === item.id}
        >
          {excluindoId === item.id ? "Excluindo..." : "Excluir"}
        </button>
      </div>
    </div>
  );
}

const css = `
.redeContainer {
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding-bottom: 42px;
  overflow-x: hidden;
}

.redeHero {
  background:
    radial-gradient(circle at 20% 0%, rgba(59,130,246,0.30), transparent 30%),
    radial-gradient(circle at 90% 10%, rgba(168,85,247,0.22), transparent 32%),
    linear-gradient(145deg, #020617, #0f172a);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 28px;
  padding: 26px;
  color: white;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  box-shadow: 0 24px 60px rgba(15,23,42,0.24);
  margin-bottom: 18px;
}

.eyebrow {
  display: inline-flex;
  margin-bottom: 8px;
  color: #93c5fd;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.redeHero h1 {
  margin: 0;
  font-size: clamp(30px, 7vw, 48px);
  letter-spacing: -0.04em;
}

.redeHero p {
  margin: 9px 0 0;
  color: #cbd5e1;
  max-width: 620px;
  line-height: 1.45;
}

.btnAtualizar {
  border: none;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: white;
  padding: 14px 20px;
  border-radius: 16px;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 16px 35px rgba(37, 99, 235, 0.28);
  white-space: nowrap;
}

.metricGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.metricCard {
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(226,232,240,0.9);
  box-shadow: 0 16px 34px rgba(15,23,42,0.08);
  border-radius: 22px;
  padding: 18px;
}

.metricCard span {
  display: block;
  color: #64748b;
  font-size: 13px;
  font-weight: 900;
  margin-bottom: 8px;
}

.metricCard strong {
  color: #0f172a;
  font-size: 30px;
  letter-spacing: -0.04em;
}

.metricCard p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.metricCard.destaque {
  background:
    radial-gradient(circle at top right, rgba(239,68,68,0.20), transparent 35%),
    linear-gradient(145deg, #fff, #f8fafc);
}

.boxPrincipal {
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 35%),
    linear-gradient(145deg, #0f172a, #020617);
  color: white;
  padding: 20px;
  border-radius: 24px;
  margin-bottom: 16px;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.22);
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatarEmpresa,
.usuarioAvatar {
  width: 54px;
  height: 54px;
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: white;
  font-weight: 1000;
  font-size: 22px;
  box-shadow: 0 12px 24px rgba(37,99,235,0.22);
}

.avatarEmpresa img,
.usuarioAvatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.boxPrincipal strong {
  display: block;
  font-size: 21px;
  margin-bottom: 4px;
}

.boxPrincipal p {
  margin: 0 0 8px;
  color: #cbd5e1;
  word-break: break-word;
}

.boxPrincipal span {
  display: inline-block;
  background: rgba(255,255,255,0.10);
  color: #dbeafe;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
}

.barraBusca {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.barraBusca input {
  width: 100%;
  border: 1px solid #e2e8f0;
  background: white;
  color: #0f172a;
  min-height: 52px;
  border-radius: 18px;
  padding: 0 16px;
  font-size: 15px;
  outline: none;
  box-shadow: 0 10px 22px rgba(15,23,42,0.06);
}

.barraBusca input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
}

.alertaPendencia {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #be123c;
  min-height: 52px;
  padding: 0 14px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 900;
  white-space: nowrap;
}

.bolinha {
  width: 10px;
  height: 10px;
  background: #ef4444;
  border-radius: 999px;
  box-shadow: 0 0 0 5px rgba(239,68,68,0.14);
}

.estadoBox {
  background: white;
  color: #64748b;
  padding: 22px;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15,23,42,0.08);
}

.arvoreBox,
.listaBusca {
  margin-top: 12px;
  width: 100%;
  overflow-x: hidden;
}

.noWrapper {
  margin-left: calc(var(--nivel) * 22px);
  max-width: 100%;
}

.redeCard {
  position: relative;
  background:
    radial-gradient(circle at top right, rgba(37,99,235,0.08), transparent 30%),
    rgba(255,255,255,0.96);
  padding: 17px;
  border-radius: 22px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
  box-shadow: 0 16px 34px rgba(15,23,42,0.10);
  border: 1px solid #eef2f7;
  max-width: 100%;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.redeCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 45px rgba(15,23,42,0.14);
  border-color: #bfdbfe;
}

.contadorPendencia {
  position: absolute;
  top: -8px;
  right: -6px;
  width: 25px;
  height: 25px;
  background: #ef4444;
  color: white;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(239,68,68,0.35);
  border: 2px solid white;
}

.usuarioInfo {
  min-width: 0;
}

.usuarioInfo strong {
  display: block;
  color: #0f172a;
  font-size: 17px;
  margin-bottom: 5px;
}

.usuarioInfo p {
  margin: 0;
  color: #64748b;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
}

.badge.status.ativo {
  background: #dcfce7;
  color: #166534;
}

.badge.status.pendente {
  background: #fef3c7;
  color: #92400e;
}

.badge.cargo {
  background: #dbeafe;
  color: #1e40af;
}

.badge.plano {
  background: #f1f5f9;
  color: #334155;
}

.badge.pendente {
  background: #fee2e2;
  color: #b91c1c;
}

.acoesUsuario {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btnAbrir,
.btnExcluir {
  border: none;
  color: white;
  padding: 10px 13px;
  border-radius: 13px;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
}

.btnAbrir {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.22);
}

.btnExcluir {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  box-shadow: 0 10px 22px rgba(239, 68, 68, 0.22);
}

.btnExcluir:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (max-width: 860px) {
  .redeHero {
    flex-direction: column;
    align-items: stretch;
    border-radius: 24px;
  }

  .btnAtualizar {
    width: 100%;
    min-height: 50px;
  }

  .metricGrid {
    grid-template-columns: 1fr;
  }

  .barraBusca {
    grid-template-columns: 1fr;
  }

  .alertaPendencia {
    justify-content: center;
    white-space: normal;
  }

  .noWrapper {
    margin-left: 0 !important;
    padding-left: calc(var(--nivel) * 10px);
  }

  .redeCard {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    padding: 16px;
    border-radius: 18px;
  }

  .acoesUsuario {
    grid-column: 1 / -1;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .btnAbrir,
  .btnExcluir {
    min-height: 46px;
    width: 100%;
  }
}

@media (max-width: 430px) {
  .redeContainer {
    padding-left: 0;
    padding-right: 0;
  }

  .redeHero,
  .boxPrincipal,
  .metricCard,
  .redeCard {
    border-radius: 18px;
  }

  .redeHero {
    padding: 20px;
  }

  .redeHero h1 {
    font-size: 32px;
  }

  .boxPrincipal {
    padding: 15px;
  }

  .avatarEmpresa,
  .usuarioAvatar {
    width: 48px;
    height: 48px;
    border-radius: 15px;
  }

  .noWrapper {
    padding-left: calc(var(--nivel) * 6px);
  }

  .acoesUsuario {
    grid-template-columns: 1fr;
  }
}
`;